import { authStorage } from "../config/session-storage";

export const WORK_FOR_STORAGE_KEY = "PennWorkFor";

export type StoredWorkFor =
  | { kind: "self" }
  | {
      kind: "registered";
      userId: string;
      email: string;
      name?: string;
      company?: string;
    }
  | { kind: "other"; email: string };

export function readStoredWorkFor(): StoredWorkFor {
  const raw = authStorage.getItem(WORK_FOR_STORAGE_KEY);
  if (!raw) return { kind: "self" };
  try {
    const parsed = JSON.parse(raw) as StoredWorkFor;
    if (parsed?.kind === "registered" && parsed.userId && parsed.email) {
      return parsed;
    }
    if (parsed?.kind === "other" && parsed.email) {
      return { kind: "other", email: String(parsed.email).trim().toLowerCase() };
    }
  } catch {
    /* ignore */
  }
  return { kind: "self" };
}

// The email + optional name/company to surface in the "Working for" banner.
// null when acting as self (banner stays hidden).
export function workForDisplay(
  stored: StoredWorkFor,
): { email: string; name?: string; company?: string } | null {
  if (stored.kind === "registered") {
    return { email: stored.email, name: stored.name, company: stored.company };
  }
  if (stored.kind === "other") {
    return { email: stored.email };
  }
  return null;
}

export function writeStoredWorkFor(value: StoredWorkFor): void {
  if (value.kind === "self") {
    authStorage.removeItem(WORK_FOR_STORAGE_KEY);
    return;
  }
  authStorage.setItem(WORK_FOR_STORAGE_KEY, JSON.stringify(value));
}

export function workForHeaderValues(
  stored: StoredWorkFor,
  selfUserId: string,
): { userId?: string; email?: string } {
  if (stored.kind === "registered" && stored.userId !== selfUserId) {
    return { userId: stored.userId };
  }
  if (stored.kind === "other") {
    return { email: stored.email };
  }
  return {};
}

type WorkForTaskFields = {
  createdBy?: string | null;
  workingForUserId?: string | null;
  workingForEmail?: string | null;
  workingForFirstName?: string | null;
  workingForLastName?: string | null;
  workingForCompanyName?: string | null;
  requestedByEmail?: string | null;
  requestedByFirstName?: string | null;
  requestedByLastName?: string | null;
  requestedByCompanyName?: string | null;
};

// Work-For context implied by loading a task into the selector (POC
// applyTaskSnapshot parity, R-5): registered target → that user, captured
// "Other" email → that email, else the task's creator when it isn't the
// opener — so a re-quote stays on the original requester's behalf. Returns
// null when the current context must be left untouched (own task, or the
// target IS the opener).
export function workForFromTask(
  task: WorkForTaskFields,
  selfUserId: string,
  selfEmail: string,
): Exclude<StoredWorkFor, { kind: "self" }> | null {
  const self = selfEmail.trim().toLowerCase();
  const wfEmail = (task.workingForEmail ?? "").trim().toLowerCase();
  if (task.workingForUserId) {
    if (task.workingForUserId === selfUserId || !wfEmail) return null;
    return {
      kind: "registered",
      userId: task.workingForUserId,
      email: wfEmail,
      name: joinName(task.workingForFirstName, task.workingForLastName),
      company: task.workingForCompanyName ?? "",
    };
  }
  if (wfEmail) {
    return wfEmail === self ? null : { kind: "other", email: wfEmail };
  }
  if (task.createdBy && task.createdBy !== selfUserId) {
    const reqEmail = (task.requestedByEmail ?? "").trim().toLowerCase();
    if (!reqEmail || reqEmail === self) return null;
    return {
      kind: "registered",
      userId: task.createdBy,
      email: reqEmail,
      name: joinName(task.requestedByFirstName, task.requestedByLastName),
      company: task.requestedByCompanyName ?? "",
    };
  }
  return null;
}

function joinName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(" ").trim();
}
